import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { InputField } from "components/inputs/InputField"
import { TitledCheckbox } from "components/inputs/TitledCheckbox"
import { Modal } from "components/modals/Modal"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { useTimezone } from "hooks/useTimezone"

import { countryInfoList, Genders, roles } from "constants/data"
import { GatewayOptions } from "constants/deposit-gateway"
import { PERMISSIONS } from "constants/permissions"
import { formatRoleName } from "constants/roles"

import { RoleLookup, roleManagementService } from "services/role-management/role-management.service"
import { settingsService } from "services/settings/settings.service"
import { VipSettings } from "services/settings/type"
import { BUILTIN_ROLE_IDS, EditUserPayload, GetSingleUser } from "services/user/type"
import { userService } from "services/user/user.service"

import { CardGateway } from "types/api"

import { shallowChanged } from "util/shallowChanged"

interface FormValuesType {
  id: string
  username: string
  first_name: string
  last_name: string
  email_address: string
  phone_number: string
  dob: string
  gender: string
  country: string
  city: string
  address: string
  address2: string
  zip: string
  // activated: boolean
  email_verified: boolean
  phone_number_verified: boolean
  kyc: boolean
  /** Selected role id — built-in enum value string OR custom role UUID. */
  role_id: string
  vipTierId?: string // used when tiers flow is on
  rakeback_balance: number
  next_card_gateway: CardGateway
  exclusion_period?: string
  stag: string
  affiliate_code: string
}

const exclusionPeriods = [
  { name: "", value: "0" },
  { name: "1 Day", value: "1" },
  { name: "7 Days", value: "7" },
  { name: "14 Days", value: "14" },
  { name: "30 Days", value: "30" },
  { name: "90 Days", value: "90" },
  { name: "180 Days", value: "180" },
  { name: "365 Days", value: "365" },
  { name: "1825 Days", value: "1825" },
  { name: "Permanent", value: "permanent" }
]

const validationSchema = Yup.object().shape({
  first_name: Yup.string().matches(/^[a-z\s]+$/i, "Only English letters"),
  last_name: Yup.string().matches(/^[a-z\s]+$/i, "Only English letters"),
  phone_number: Yup.string(),
  country: Yup.string().required(),
  exclusion_period: Yup.string().nullable(),
  vipTierId: Yup.string().optional()
})

type ModalEditUserType = {
  open: boolean
  handler: () => void
  user: GetSingleUser
  onSuccess: () => void
}

export const ModalEditUser = ({ open, handler, user, onSuccess }: ModalEditUserType) => {
  const { user: appUser } = useProps()
  const { has } = usePermissions()
  const { formatDate } = useTimezone()
  const [vipSettings, setVipSettings] = useState<VipSettings | null>(null)
  const [vipLoading, setVipLoading] = useState(false)
  const [allRoles, setAllRoles] = useState<RoleLookup[]>([])

  useEffect(() => {
    const loadVip = async () => {
      if (!open) return
      setVipLoading(true)
      const vip = await settingsService.getVip()
      setVipSettings(vip)
      setVipLoading(false)
    }
    loadVip()
  }, [open])

  useEffect(() => {
    if (!open || !has(PERMISSIONS.USER_EDIT_ROLE)) return
    roleManagementService
      .listRolesLookup()
      .then((rs) => rs && setAllRoles(rs))
      .catch(() => {
        // Non-fatal — fall back to built-in roles via the imported `roles` constant.
      })
  }, [open])

  const tierFlowEnabled = useMemo(() => (vipSettings?.tiers ?? []).length > 0, [vipSettings])

  const tierOptions = useMemo(() => {
    const tiers = (vipSettings?.tiers ?? []).filter((t) => !t.invitationOnly).sort((a, b) => a.minTotalWagerUsd - b.minTotalWagerUsd)

    return [{ name: "", value: "" }, ...tiers.map((t) => ({ name: `${t.name} (≥ $${t.minTotalWagerUsd.toLocaleString()})`, value: t.id }))]
  }, [vipSettings])

  // Role dropdown options — prefers the fetched list of all roles (built-in +
  // custom) and falls back to the static built-in list if the fetch failed.
  // ADMIN can only be assigned by another ADMIN.
  const roleOptions = useMemo(() => {
    if (allRoles.length > 0) {
      return allRoles.map((r) => ({
        name: r.is_builtin ? formatRoleName(r.id) : `${r.name} (custom)`,
        value: r.id,
        disable: r.id === BUILTIN_ROLE_IDS.ADMIN && appUser?.role !== BUILTIN_ROLE_IDS.ADMIN
      }))
    }
    return roles.map((role) => ({
      ...role,
      disable: role.name === BUILTIN_ROLE_IDS.ADMIN && appUser?.role !== BUILTIN_ROLE_IDS.ADMIN
    }))
  }, [allRoles, appUser?.role])

  const vip = user.vipProgress
  const hasVipTier = !!vip && vip.tierId !== null

  const formik = useFormik<FormValuesType>({
    initialValues: {
      id: user.id,
      username: user.username,
      first_name: user.personal_profile.first_name ?? "",
      last_name: user.personal_profile.last_name ?? "",
      email_address: user.email_address,
      phone_number: user.phone_number ?? "",
      dob: user.personal_profile.dob ?? "",
      gender: user.personal_profile.gender ?? "",
      country: user.personal_profile.country ?? "",
      city: user.personal_profile.city ?? "",
      address: user.personal_profile.address ?? "",
      address2: user.personal_profile.address2 ?? "",
      zip: user.personal_profile.zip ?? "",
      email_verified: user.email_verified,
      phone_number_verified: user.phone_number_verified,
      kyc: user.kyc,
      role_id: user.effective_role?.id ?? user.role,
      vipTierId: hasVipTier ? user.vipProgress?.tierId ?? undefined : undefined,
      rakeback_balance: user.rakeback_balance,
      next_card_gateway: user.next_card_gateway ?? CardGateway.FV0,
      exclusion_period: "",
      stag: user.stag ?? "",
      affiliate_code: user.referred_by?.affiliate_code ?? ""
    },
    enableReinitialize: true,
    onSubmit: async ({ id, ...values }: EditUserPayload & { id: string }) => {
      try {
        if (!formik.dirty) return
        const payload: Partial<EditUserPayload & { vipTierId?: string }> = { ...values }

        if (tierFlowEnabled) {
          if (!payload.vipTierId) delete (payload as any).vipTierId
        } else {
          delete (payload as any).vipTierId
        }

        const changedValues = shallowChanged(payload, formik.initialValues as any)
        if (changedValues.first_name || changedValues.last_name) {
          changedValues.display_name = values.first_name + " " + values.last_name
        }

        await userService.editUser(id, changedValues)
        toast.success("Successfully updated user")
        handler()
        onSuccess()
      } catch (error: any) {
        toast.error(error?.data?.message ?? error?.message ?? "Failed update")
      }
    },
    validationSchema
  })

  return (
    <Modal open={open} handler={handler}>
      <div className="flex flex-col modal-box w-full max-w-full">
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <InputField id="username" title="Username" value={formik.values.username} disabled />
          <InputField id="email_address" title="Email" value={formik.values.email_address} onChange={formik.handleChange} />
          <InputField
            name="first_name"
            title="First Name"
            value={formik.values.first_name}
            onChange={formik.handleChange}
            invalid={!!(formik.errors.first_name && formik.touched.first_name)}
            {...(formik.errors.first_name && formik.touched.first_name && { altLabelTwo: formik.errors.first_name })}
          />
          <InputField
            name="last_name"
            title="Last Name"
            value={formik.values.last_name}
            onChange={formik.handleChange}
            invalid={!!(formik.errors.last_name && formik.touched.last_name)}
            {...(formik.errors.last_name && formik.touched.last_name && { altLabelTwo: formik.errors.last_name })}
          />
          <DropdownMenu
            id="next_card_gateway"
            title="Deposit Gateway"
            placeholder="Select a Gateway"
            disableEmptyOption
            options={GatewayOptions}
            value={formik.values.next_card_gateway}
            onChange={formik.handleChange}
          />
          <InputField
            id="phone_number"
            title="Phone number"
            value={formik.values.phone_number}
            onChange={formik.handleChange}
            invalid={!!(formik.errors.phone_number && formik.touched.phone_number)}
            {...(formik.errors.phone_number && formik.touched.phone_number && { altLabelTwo: formik.errors.phone_number })}
          />
          <div>
            <label className="label">
              <span className="label-text">Role</span>
            </label>
            <DropdownMenu
              id="role_id"
              options={roleOptions}
              value={formik.values.role_id}
              onChange={formik.handleChange}
              bgColor="transparent"
              disabled={!has(PERMISSIONS.USER_EDIT_ROLE)}
            />
          </div>
          <DropdownMenu
            id="gender"
            title="Gender"
            options={Genders}
            value={formik.values.gender}
            onChange={formik.handleChange}
            bgColor="transparent"
            placeholder="Please choose an option"
          />
          <InputField name="dob" type="date" title="Birthday" value={formik.values.dob} onChange={formik.handleChange} />
          <div className="lg:max-w-[250px]">
            <DropdownMenu
              id="country"
              title="Country"
              options={countryInfoList}
              value={formik.values.country}
              onChange={formik.handleChange}
              bgColor="transparent"
              invalid={!!(formik.errors.country && formik.touched.country)}
              {...(formik.errors.country && formik.touched.country && { altLabelOne: formik.errors.country })}
              placeholder="Please choose an option"
            />
          </div>
          <InputField id="city" title="City" value={formik.values.city} onChange={formik.handleChange} />
          <InputField id="address" title="Street1" value={formik.values.address} onChange={formik.handleChange} />
          <InputField id="address2" title="Street2" value={formik.values.address2} onChange={formik.handleChange} />
          <InputField name="zip" title="Zipcode" value={formik.values.zip} onChange={formik.handleChange} />
          <InputField id="stag" title="Stag" value={formik.values.stag} onChange={formik.handleChange} />
          <InputField
            id="affiliate_code"
            title={`Affiliate Code${user.referred_by ? ` (${user.referred_by.username})` : ""}`}
            value={formik.values.affiliate_code}
            onChange={formik.handleChange}
            placeholder="Affiliate code"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-10">
          {has(PERMISSIONS.USER_EDIT_RAKEBACK) ? (
            <>
              {/* <InputField
                id="rakeback_balance"
                title="Rakeback balance"
                type="number"
                value={formik.values.rakeback_balance}
                onChange={formik.handleChange}
                altLabelOne={`$${formik.values.rakeback_balance / 100}`}
                invalid={!!(formik.errors.rakeback_balance && formik.touched.rakeback_balance)}
                altLabelTwo={formik.touched.rakeback_balance ? formik.errors.rakeback_balance : ""}
              /> */}
              {tierFlowEnabled ? (
                <DropdownMenu
                  id="vipTierId"
                  title={vipLoading ? "VIP Tier (loading...)" : "VIP Tier"}
                  options={tierOptions}
                  value={formik.values.vipTierId ?? ""}
                  onChange={(e: any) => formik.setFieldValue("vipTierId", e.target.value || undefined)}
                  bgColor="transparent"
                  placeholder="Select VIP tier"
                />
              ) : null}
            </>
          ) : null}
          <DropdownMenu
            id="exclusion_period"
            title={`Exclusion Period (${
              !user.activated
                ? "Permanently Excluded"
                : user.exclusion_end
                ? `Excluded Until ${formatDate(user.exclusion_end)}`
                : "Not Excluded"
            })`}
            options={exclusionPeriods}
            placeholder=""
            hideEmptyOption
            value={formik.values.exclusion_period || ""}
            onChange={(event: any) => {
              formik.setFieldValue("exclusion_period", event.target.value)
            }}
          />
          <button
            type="button"
            className="btn btn-warning w-full self-end"
            onClick={() => {
              formik.setFieldValue("exclusion_period", "0")
            }}
          >
            Remove Exclusion
          </button>
        </div>
        <div className="flex flex-col mt-4 justify-start w-1/4">
          <TitledCheckbox
            id="phone_number_verified"
            title="Phone verified"
            checked={formik.values.phone_number_verified}
            onChange={formik.handleChange}
          />
          <TitledCheckbox
            id="email_verified"
            title="Email verified"
            checked={formik.values.email_verified}
            onChange={formik.handleChange}
          />
          <TitledCheckbox id="kyc" title="KYC" checked={formik.values.kyc} onChange={formik.handleChange} />
        </div>

        <div className="modal-action">
          <button className="btn btn-secondary" onClick={handler}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={formik.submitForm} disabled={!formik.dirty || formik.isSubmitting}>
            Save
          </button>
        </div>
      </div>
    </Modal>
  )
}
